import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Award,
  Facebook,
  ExternalLink,
  Shield,
  Star,
  BadgeCheck,
  Building2,
  Scale
} from 'lucide-react';

const Footer = () => {
  const scrollToSection = (sectionId: string) => {
    if (window.location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
    } else {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-dk-dark-blue via-dk-blue to-dk-blue text-white">
      <div className="container mx-auto px-4 py-12">
        {/* Trust Badges Section */}
        <div className="mb-12 border-b border-white/20 pb-8">
          <div className="text-center mb-8">
            <h3 className="text-xl font-semibold mb-2">Ils nous font confiance</h3>
            <p className="text-white/80">Certifications et accréditations</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 items-center justify-items-center">
            <div className="flex flex-col items-center text-center p-4 bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
              <BadgeCheck className="h-12 w-12 mb-3 text-white" />
              <span className="font-medium">Diagnostiqueur Certifié</span>
              <span className="text-sm text-white/80">Certification DPE</span>
            </div>
            <div className="flex flex-col items-center text-center p-4 bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
              <Scale className="h-12 w-12 mb-3 text-white" />
              <span className="font-medium">Assurance RCP</span>
              <span className="text-sm text-white/80">Garantie professionnelle</span>
            </div>
            <div className="flex flex-col items-center text-center p-4 bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
              <Star className="h-12 w-12 mb-3 text-white" />
              <span className="font-medium">Note 4.9/5</span>
              <span className="text-sm text-white/80">Avis clients vérifiés</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Company Info */}
          <div>
            <div className="mb-6">
              <img 
                src="/logo.png"
                alt="DIAGNOSTI-K Logo"
                className="h-20 w-auto object-contain brightness-0 invert"
              />
            </div>

            <p className="mb-4">
              Expert en diagnostics immobiliers à Camaret-sur-Aigues. Diagnostics électricité, gaz, amiante et plomb.
            </p>
            <div className="flex items-center space-x-2 mb-2">
              <Award className="h-5 w-5 text-white" />
              <span>Diagnostiqueur certifié</span>
            </div>

            {/* Certifications */}
            <div className="mt-6 bg-white/10 p-4 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center">
                <Building2 className="h-5 w-5 mr-2" />
                Nos certifications
              </h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center">
                  <Star className="h-4 w-4 mr-2 text-green-400" />
                  Certification DPE
                </li>
              </ul>
            </div>

            {/* Social Media */}
            <div className="mt-6">
              <p className="font-semibold mb-3">Suivez-nous</p>
              <div className="flex space-x-4">
                <a 
                  href="https://www.facebook.com/SarlDgK" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors" 
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-lg font-semibold mb-4 border-b border-white/20 pb-2">Navigation</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="hover:text-white/80 transition-colors flex items-center">
                  <ExternalLink className="h-4 w-4 mr-2 text-white" />
                  Accueil
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-white/80 transition-colors flex items-center">
                  <ExternalLink className="h-4 w-4 mr-2 text-white" />
                  Services
                </Link>
              </li>
              <li>
                <Link to="/audit-energetique" className="hover:text-white/80 transition-colors flex items-center">
                  <ExternalLink className="h-4 w-4 mr-2 text-white" />
                  Audit Énergétique
                </Link>
              </li>
              <li>
                <Link to="/realisations" className="hover:text-white/80 transition-colors flex items-center">
                  <ExternalLink className="h-4 w-4 mr-2 text-white" />
                  Réalisations
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white/80 transition-colors flex items-center">
                  <ExternalLink className="h-4 w-4 mr-2 text-white" />
                  Contact
                </Link>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('devis')} 
                  className="hover:text-white/80 transition-colors flex items-center"
                >
                  <ExternalLink className="h-4 w-4 mr-2 text-white" />
                  Demander un devis
                </button>
              </li>
            </ul>

            <div className="mt-6">
              <h4 className="font-semibold mb-2">Liens utiles</h4>
              <ul className="space-y-1 text-sm">
                <li>
                  <Link to="/mentions-legales" className="hover:text-white/80 transition-colors">
                    Mentions légales
                  </Link>
                </li>
                <li>
                  <Link to="/politique-confidentialite" className="hover:text-white/80 transition-colors">
                    Politique de confidentialité
                  </Link>
                </li>
                <li>
                  <Link to="/conditions-generales" className="hover:text-white/80 transition-colors">
                    Conditions générales
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4 border-b border-white/20 pb-2">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Phone className="h-5 w-5 text-white mt-0.5" />
                <div>
                  <p className="font-medium">Téléphone</p>
                  <a href="tel:0789307500" className="hover:text-white/80 transition-colors">
                    07 89 30 75 00
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-white mt-0.5" />
                <div>
                  <p className="font-medium">Email</p>
                  <a href="mailto:Spskdiag@gmail.com" className="hover:text-white/80 transition-colors">
                    Spskdiag@gmail.com
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-white mt-0.5" />
                <div>
                  <p className="font-medium">Adresse</p>
                  <p>95 avenue Fernand Gonnet</p>
                  <p>84850 Camaret-sur-Aigues</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Clock className="h-5 w-5 text-white mt-0.5" />
                <div>
                  <p className="font-medium">Horaires</p>
                  <p>Lun-Ven: 8h-19h</p>
                  <p>Sam: Sur rendez-vous</p>
                </div>
              </li>
            </ul>

            <div className="mt-6">
              <a href="tel:0789307500" className="btn bg-white/10 hover:bg-white/20 w-full flex items-center justify-center">
                Nous contacter
                <Phone className="h-4 w-4 ml-2" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/20 mt-10 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm mb-4 md:mb-0">
              <p className="mb-2">
                &copy; {currentYear} DIAGNOSTI-K. Tous droits réservés.
              </p>
              <div className="flex flex-wrap gap-2 text-xs text-white/60">
                <Link to="/diagnostic-valence-drome" className="hover:text-white/80 transition-colors">
                  Diagnostic Valence
                </Link>
                <span>•</span>
                <Link to="/diagnostic-ardeche-saint-peray" className="hover:text-white/80 transition-colors">
                  Diagnostic Ardèche
                </Link>
                <span>•</span>
                <Link to="/diagnostic-orange-vaucluse" className="hover:text-white/80 transition-colors">
                  Diagnostic Orange
                </Link>
                <span>•</span>
                <Link to="/diagnostic-avignon-region" className="hover:text-white/80 transition-colors">
                  Diagnostic Avignon
                </Link>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <span>Expert en diagnostics immobiliers</span>
              <Award className="h-4 w-4 text-white" />
              <span>Certifié</span>
            </div>
          </div>
          <div className="text-center mt-4 text-xs text-white/60">
            <a 
              href="https://easywebcreation.fr" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-white/80 transition-colors"
            >
              Créé par easywebcreation.fr
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;